/**
 * BiomeNexusReturn — session-scoped "Return to Biome Nexus" button.
 *
 * The /biomes showcase ("Biome Nexus" card) is a vibe gallery: its six tour
 * pads warp you OUT into the biome example maps so you can soak in each
 * atmosphere regardless of level (the pads set `bypassZoneGate`). Those pads
 * point one-way — the biome maps themselves are NOT edited, so normal
 * progression keeps its level gating untouched. This floating button is the
 * way back: it warps to the hub via the same EventBus 'requestMapSwitch' path
 * the pads + MapPickerOverlay use.
 *
 * Scoping (why it never leaks into normal play): a tour is marked by the
 * `helix_biome_tour` sessionStorage flag, set only by the /biomes coercion and
 * cleared on entering any other mode (see applyModeUrlCoercion.ts). Cross-map
 * warps are full page navigations (SceneEventManager → window.location.assign),
 * so this is a pure read at mount — show iff the tour flag is set AND the
 * current map (`?map=`) is one of the biome worlds (i.e. you've left the hub).
 * Reaching a biome through normal /adventure progression never sets the flag,
 * so the ungated hop-back is invisible there.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { EventBus } from '../../../shared/EventBus';
import { bootSearchParams } from '../../../config/boot/bootUrl';

const NEXUS_MAP_ID = 'biome_nexus';

/** The biome worlds the Nexus tour pads warp to. */
const BIOME_MAP_IDS = new Set([
  'volcano_summit',
  'frozen_lake',
  'crystal_caverns',
  'sunken_ruins',
  'ancient_temple',
  'coil_undercroft',
]);

function shouldShow(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem('helix_biome_tour') !== '1') return false;
  } catch {
    return false;
  }
  const mapId = bootSearchParams().get('map');
  return !!mapId && mapId !== NEXUS_MAP_ID && BIOME_MAP_IDS.has(mapId);
}

export const BiomeNexusReturn: React.FC = () => {
  if (!shouldShow()) return null;
  if (typeof document === 'undefined') return null;

  // Portal to <body>: GameUIOverlay mounts inside #world-passthrough (the
  // full-screen world-click layer, pointer-events:auto), which would otherwise
  // swallow this button's clicks. A body-level portal floats above #root
  // entirely so the button is genuinely clickable over the game world.
  return createPortal(
    <button
      type="button"
      data-testid="biome-nexus-return"
      onClick={() => EventBus.getInstance().emit('requestMapSwitch', { mapId: NEXUS_MAP_ID })}
      style={{
        position: 'fixed',
        // Top-left, just under the unit frame. Top-CENTER is crowded in a biome
        // (class-switcher bar + level/PvP bar + the enemy target frame all live
        // there), so anchor left where nothing else competes for the clicks.
        top: 172,
        left: 18,
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        padding: '8px 16px',
        background: 'rgba(20, 16, 34, 0.88)',
        border: '1px solid #a55eea',
        borderRadius: 14,
        color: '#e7d9ff',
        fontFamily: 'Inter, sans-serif',
        textAlign: 'left',
        cursor: 'pointer',
        boxShadow: '0 4px 18px rgba(165, 94, 234, 0.35)',
        backdropFilter: 'blur(4px)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(40, 28, 64, 0.95)';
        e.currentTarget.style.boxShadow = '0 6px 22px rgba(165, 94, 234, 0.55)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(20, 16, 34, 0.88)';
        e.currentTarget.style.boxShadow = '0 4px 18px rgba(165, 94, 234, 0.35)';
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.03em' }}>
        🌀 Return to Biome Nexus
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 500, opacity: 0.7, color: '#c9b6ff' }}>
        🕊 Peaceful tour — you can’t be harmed
      </span>
    </button>,
    document.body,
  );
};
